import * as React from "react";

import { useAnalytics, useAuth } from "@/providers";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  ResourceView,
  useResourceView,
} from "@/shared/components/ResourceView";
import {
  ROW_ACTIONS_COLUMN_ID,
  RowActionsMenu,
  rowActionsColumn,
} from "@/shared/components/RowActionsMenu";
import {
  type ImageRegistryRecord,
  type ImageRepositoryRecord,
} from "@/shared/domain/actions";
import {
  useDeleteImage,
  useDeleteImageRepository,
  useImageRepositories,
  useRepositoryImages,
} from "@/shared/domain/actions/hooks";
import {
  Copy,
  Package as PackageIcon,
  Trash2,
} from "@/shared/foundation/icons";
import { Banner, ConfirmDialog } from "@/shared/foundation/primitives";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  Table,
  cellStyles,
  colId,
  deriveLoadingState,
  useCellDeps,
  type CellDeps,
  type ColDef,
  type ColumnPickerOption,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import type { SortDirection } from "@/types";

import styles from "./ImageRepositoriesList.module.css";
import { UriCell } from "./UriCell";

const PUSH_DOCS_URL =
  "https://docs.roboto.ai/getting-started/programmatic-access.html#python-sdk-and-cli";

interface Callbacks {
  copy: (text: string) => void;
  requestDeleteRepository: (repository: ImageRepositoryRecord) => void;
}

/** Columns for the repository rows; `deps` keeps the array's identity. */
function buildColumns(
  deps: CellDeps<Callbacks>,
): ColDef<ImageRepositoryRecord>[] {
  return [
    {
      colId: "repository_name",
      field: "repository_name",
      headerName: "Repository",
      flex: 2,
      minWidth: 240,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<ImageRepositoryRecord, string>) => (
        <span className={cellStyles.primaryContent}>{p.value}</span>
      ),
    },
    {
      colId: "uri",
      field: "repository_uri",
      headerName: "URI",
      flex: 3,
      minWidth: 260,
      cellRenderer: (p: ICellRendererParams<ImageRepositoryRecord, string>) => (
        <UriCell uri={p.value ?? ""} onCopy={deps.current.copy} />
      ),
    },
    rowActionsColumn<ImageRepositoryRecord>((repository) => (
      <RowActionsMenu
        label={repository.repository_name}
        actions={[
          {
            label: "Copy URI",
            icon: <Copy />,
            onClick: () => deps.current.copy(repository.repository_uri),
          },
          {
            label: "Delete repository",
            icon: <Trash2 />,
            danger: true,
            onClick: () => deps.current.requestDeleteRepository(repository),
          },
        ]}
      />
    )),
  ];
}

const DEFAULT_VISIBLE_COLUMN_IDS = ["repository_name", "uri"];

/** Newest-looking tags first: `latest`, then versions in descending order. */
function compareTags(a: ImageRegistryRecord, b: ImageRegistryRecord): number {
  if (a.image_tag === "latest") {
    return -1;
  }
  if (b.image_tag === "latest") {
    return 1;
  }
  return b.image_tag.localeCompare(a.image_tag, undefined, { numeric: true });
}

interface RepositoryImagesProps {
  repository: ImageRepositoryRecord;
  orgId: string | undefined;
  onCopy: (text: string) => void;
  onRequestDelete: (image: ImageRegistryRecord) => void;
}

/**
 * An opened repository's images. They are read live from the registry, so
 * they load when the repository is opened and not before.
 */
function RepositoryImages({
  repository,
  orgId,
  onCopy,
  onRequestDelete,
}: RepositoryImagesProps) {
  const imagesQuery = useRepositoryImages(orgId, repository.repository_name);
  const images = React.useMemo(
    () => [...(imagesQuery.data ?? [])].sort(compareTags),
    [imagesQuery.data],
  );

  if (imagesQuery.isLoading) {
    return <p className={styles.panelMessage}>Loading images…</p>;
  }
  if (imagesQuery.isError) {
    return (
      <Banner variant="error" className={styles.panelMessage}>
        Failed to load images. {imagesQuery.error.message}
      </Banner>
    );
  }
  if (images.length === 0) {
    return (
      <p className={styles.panelMessage}>No images in this repository yet.</p>
    );
  }
  return (
    <div className={styles.panel}>
      <Table aria-label={`Images in ${repository.repository_name}`}>
        <Table.Header variant="compact">
          <Table.Row>
            <Table.Head>Tag</Table.Head>
            <Table.Head>URI</Table.Head>
            <Table.Head className={styles.menuHead}>
              <span className="sr-only">Actions</span>
            </Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {images.map((image) => (
            <Table.Row key={image.image_uri}>
              <Table.Head scope="row" className={styles.tagName}>
                {image.image_tag}
              </Table.Head>
              <Table.Cell>
                <UriCell uri={image.image_uri} onCopy={onCopy} />
              </Table.Cell>
              <Table.Cell className={styles.menuCell}>
                <RowActionsMenu
                  label={`${image.repository_name}:${image.image_tag}`}
                  actions={[
                    {
                      label: "Copy URI",
                      icon: <Copy />,
                      onClick: () => onCopy(image.image_uri),
                    },
                    {
                      label: "Delete image",
                      icon: <Trash2 />,
                      danger: true,
                      onClick: () => onRequestDelete(image),
                    },
                  ]}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

/**
 * The Action Images tab: the org's private image repositories in a
 * `DataTable`. Opening a repository lists its images; deleting a repository
 * or an image goes through a confirm.
 *
 * Repositories are listed from Roboto's own records a page at a time, in the
 * name order the header asks for. Images are read live from the registry one
 * repository at a time, so the page never lists every image in the org and
 * offers no filter across repositories.
 */
export function ImageRepositoriesList() {
  const { currentOrganization } = useAuth();
  const { trackEvent } = useAnalytics();
  const showNotification = useNotification();
  const orgId = currentOrganization?.org_id;
  const deleteRepository = useDeleteImageRepository(orgId);
  const deleteImage = useDeleteImage(orgId);
  const [pendingRepository, setPendingRepository] =
    React.useState<ImageRepositoryRecord | null>(null);
  const [pendingImage, setPendingImage] =
    React.useState<ImageRegistryRecord | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const copy = React.useCallback(
    (text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => showNotification("URI copied"))
        .catch(() => showNotification("Couldn't copy the URI", "error"));
    },
    [showNotification],
  );

  const cellDeps = useCellDeps<Callbacks>({
    copy,
    requestDeleteRepository: setPendingRepository,
  });
  const [columns] = React.useState(() => buildColumns(cellDeps));
  const pickerOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns
        .filter((column) => colId(column) !== ROW_ACTIONS_COLUMN_ID)
        .map((column) => ({
          id: colId(column),
          label: column.headerName ?? colId(column),
        })),
    [columns],
  );

  const view = useResourceView<ImageRepositoryRecord>({
    getRowId: (repository) => repository.repository_name,
    initialSort: { columnId: "repository_name", direction: "asc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  // Token chain for forward paging: the entry at N is the `after` token for
  // page N, which page N-1's response handed out. Page 0 sends none. The chain
  // empties whenever the sort or page size changes, which is when
  // `useResourceView` returns the table to page 0 as well.
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    () => new Map(),
  );
  React.useEffect(() => {
    setTokenChain(new Map());
  }, [view.table.sort, view.table.pageSize]);

  const currentPage = view.table.currentPage;
  const repositoriesQuery = useImageRepositories(orgId, {
    sortDirection: (view.table.sort?.direction.toUpperCase() ??
      "ASC") as SortDirection,
    limit: view.table.pageSize,
    pageToken: currentPage > 0 ? tokenChain.get(currentPage) : undefined,
  });

  // Store the next page's token from each settled response. Keyed on the
  // response itself, not the token's text: tokens are offsets, so flipping the
  // sort on page 0 answers with the same "50" as before, and an effect
  // watching the text would never refill the chain the reset above just
  // emptied. The placeholder a refetch shows is the old response, so it is
  // skipped rather than stored under the new query.
  const settled = repositoriesQuery.isPlaceholderData
    ? undefined
    : repositoriesQuery.data;
  React.useEffect(() => {
    const token = settled?.next_token;
    if (!token) {
      return;
    }
    setTokenChain((prev) => {
      if (prev.get(currentPage + 1) === token) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, token);
      return next;
    });
  }, [settled, currentPage]);
  const nextToken = repositoriesQuery.data?.next_token ?? null;

  const rows = repositoriesQuery.data?.items ?? [];

  const renderImages = React.useCallback(
    (repository: ImageRepositoryRecord) => (
      <RepositoryImages
        repository={repository}
        orgId={orgId}
        onCopy={copy}
        onRequestDelete={setPendingImage}
      />
    ),
    [copy, orgId],
  );

  const loading = deriveLoadingState({
    isLoading: repositoriesQuery.isLoading,
    isFetching: repositoriesQuery.isFetching,
  });

  const confirmDeleteRepository = async () => {
    if (pendingRepository === null) {
      return;
    }
    setDeleteError(null);
    trackEvent("DeleteRepositoryClicked", {
      repoName: pendingRepository.repository_name,
      repoUri: pendingRepository.repository_uri,
    });
    try {
      await deleteRepository.mutateAsync(pendingRepository.repository_name);
    } catch (err) {
      setDeleteError(
        `Couldn't delete ${pendingRepository.repository_name}. ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
    setPendingRepository(null);
  };

  const confirmDeleteImage = async () => {
    if (pendingImage === null) {
      return;
    }
    setDeleteError(null);
    trackEvent("DeleteImageClicked", { imageUri: pendingImage.image_uri });
    try {
      await deleteImage.mutateAsync(pendingImage);
    } catch (err) {
      setDeleteError(
        `Couldn't delete ${pendingImage.image_uri}. ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
    setPendingImage(null);
  };

  return (
    <div className={styles.page}>
      {repositoriesQuery.isError && (
        <Banner variant="error" className={styles.banner}>
          Failed to load image repositories.{" "}
          {repositoriesQuery.error?.message ?? ""}
        </Banner>
      )}
      {deleteError !== null && (
        <Banner variant="error" className={styles.banner}>
          {deleteError}
        </Banner>
      )}
      <ResourceView.Root>
        <ResourceView.Actions>
          <ColumnVisibility
            options={pickerOptions}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
        </ResourceView.Actions>
        <DataTable<ImageRepositoryRecord>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={nextToken !== null}
          loading={loading}
          columns={columns}
          data={rows}
          ariaLabel="Action images"
          expansion={{ renderRow: renderImages }}
          noRowsContent={
            <ResourceView.EmptyState
              hasActiveFilters={false}
              noData={{
                icon: PackageIcon,
                title: "No images yet",
                description: (
                  <>
                    Images are Docker containers holding an action&apos;s code.
                    Push one to Roboto&apos;s private registry with the{" "}
                    <a href={PUSH_DOCS_URL} target="_blank" rel="noreferrer">
                      Roboto CLI
                    </a>
                    : <code>roboto images push &lt;image-name:1.2.3&gt;</code>
                  </>
                ),
              }}
              noMatches={{
                icon: PackageIcon,
                title: "No matching repositories",
                description: "Try adjusting or clearing your filters.",
              }}
            />
          }
        />
      </ResourceView.Root>
      <ConfirmDialog
        open={pendingRepository !== null}
        onOpenChange={(open) => {
          if (!open && !deleteRepository.isPending) {
            setPendingRepository(null);
          }
        }}
        title={`Delete ${pendingRepository?.repository_name ?? "repository"}?`}
        description="The repository and every image in it are removed. This cannot be undone."
        confirmLabel="Delete"
        destructive
        pending={deleteRepository.isPending}
        onConfirm={() => void confirmDeleteRepository()}
      />
      <ConfirmDialog
        open={pendingImage !== null}
        onOpenChange={(open) => {
          if (!open && !deleteImage.isPending) {
            setPendingImage(null);
          }
        }}
        title={`Delete ${pendingImage?.repository_name ?? ""}:${
          pendingImage?.image_tag ?? ""
        }?`}
        description={pendingImage?.image_uri}
        confirmLabel="Delete"
        destructive
        pending={deleteImage.isPending}
        onConfirm={() => void confirmDeleteImage()}
      />
    </div>
  );
}
